import { useTranslation } from 'react-i18next';

import { FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';

import type { AttributeType, AttributeValidationRules } from '../../model/types';

interface AttributeValidationRulesEditorProps {
  type: AttributeType;
  value: AttributeValidationRules;
  onChange: (value: AttributeValidationRules) => void;
  disabled?: boolean;
}

/**
 * Type-dependent validation-rules editor (user-story-catalog-013, AC3/AC4): TEXT gets
 * regex/maxLength/GTIN-checksum, NUMBER and MEASUREMENT share min/max/decimals (MEASUREMENT's are
 * in the attribute's measurement family's standard unit), DATE gets min/max date. BOOLEAN/SELECT/
 * MULTISELECT have no validation rules here (SELECT/MULTISELECT get the options manager instead).
 */
export function AttributeValidationRulesEditor({
  type,
  value,
  onChange,
  disabled,
}: AttributeValidationRulesEditorProps) {
  const { t } = useTranslation('catalog');

  const set = <K extends keyof AttributeValidationRules>(key: K, next: AttributeValidationRules[K]) =>
    onChange({ ...value, [key]: next });

  const toOptionalNumber = (raw: string): number | undefined => {
    if (raw.trim() === '') return undefined;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? undefined : parsed;
  };

  if (type === 'TEXT') {
    return (
      <Stack spacing={2}>
        <Typography variant="subtitle2">{t('attributeValidationRulesEditor.title')}</Typography>
        <TextField
          label={t('attributeValidationRulesEditor.regexLabel')}
          value={value.regex ?? ''}
          onChange={(e) => set('regex', e.target.value || undefined)}
          disabled={disabled}
          fullWidth
          size="small"
        />
        <TextField
          label={t('attributeValidationRulesEditor.maxLengthLabel')}
          type="number"
          value={value.maxLength ?? ''}
          onChange={(e) => set('maxLength', toOptionalNumber(e.target.value))}
          disabled={disabled}
          fullWidth
          size="small"
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={value.gtin ?? false}
              onChange={(e) => set('gtin', e.target.checked)}
              disabled={disabled}
            />
          }
          label={t('attributeValidationRulesEditor.gtinLabel')}
        />
      </Stack>
    );
  }

  if (type === 'NUMBER' || type === 'MEASUREMENT') {
    return (
      <Stack spacing={2}>
        <Typography variant="subtitle2">{t('attributeValidationRulesEditor.title')}</Typography>
        {type === 'MEASUREMENT' ? (
          <Typography variant="caption" color="text.secondary">
            {t('attributeValidationRulesEditor.measurementHint')}
          </Typography>
        ) : null}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('attributeValidationRulesEditor.minValueLabel')}
            type="number"
            value={value.minValue ?? ''}
            onChange={(e) => set('minValue', toOptionalNumber(e.target.value))}
            disabled={disabled}
            fullWidth
            size="small"
          />
          <TextField
            label={t('attributeValidationRulesEditor.maxValueLabel')}
            type="number"
            value={value.maxValue ?? ''}
            onChange={(e) => set('maxValue', toOptionalNumber(e.target.value))}
            disabled={disabled}
            fullWidth
            size="small"
          />
        </Stack>
        <TextField
          label={t('attributeValidationRulesEditor.decimalsLabel')}
          type="number"
          value={value.decimals ?? ''}
          onChange={(e) => set('decimals', toOptionalNumber(e.target.value))}
          disabled={disabled}
          fullWidth
          size="small"
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
        />
      </Stack>
    );
  }

  if (type === 'DATE') {
    return (
      <Stack spacing={2}>
        <Typography variant="subtitle2">{t('attributeValidationRulesEditor.title')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('attributeValidationRulesEditor.minDateLabel')}
            type="date"
            value={value.minDate ?? ''}
            onChange={(e) => set('minDate', e.target.value || undefined)}
            disabled={disabled}
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('attributeValidationRulesEditor.maxDateLabel')}
            type="date"
            value={value.maxDate ?? ''}
            onChange={(e) => set('maxDate', e.target.value || undefined)}
            disabled={disabled}
            fullWidth
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Stack>
    );
  }

  return null;
}
